import { createClient } from '@supabase/supabase-js';
import { Logger } from '../utils/logger';
import { SingaporeTime } from '../utils/unified-date';

// Ensure we're in the browser environment
if (typeof window === 'undefined') {
  throw new Error('This application is designed to run in the browser environment');
}

// Get configuration from environment variables
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate required environment variables
if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error(
    'Missing required Supabase environment variables. Please ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set in your .env file.'
  );
}

// Create a custom fetch function that adds logging
const createCustomFetch = () => {
  const originalFetch = window.fetch;

  return async (url: string | URL | Request, options?: RequestInit) => {
    try {
      const response = await originalFetch(url, options);

      // Only log errors that are not expected 400s from task operations
      if (!response.ok) {
        const urlString = url.toString();
        const isTaskUpdate = urlString.includes('/tasks?id=eq.');
        const is400Error = response.status === 400;

        // Don't log 400 errors for task updates (these happen when updating non-existent tasks)
        if (!(isTaskUpdate && is400Error)) {
          Logger.warn('Supabase request failed', {
            url: urlString,
            status: response.status,
            statusText: response.statusText,
            timestamp: SingaporeTime.now(),
          });
        }
      }

      return response;
    } catch (error) {
      Logger.error('Supabase request error', {
        url: url.toString(),
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: SingaporeTime.now(),
      });
      throw error;
    }
  };
};

// Create and configure the Supabase client with optimized settings
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  global: {
    fetch: createCustomFetch(),
  },
  auth: {
    // Enable automatic token refresh for seamless user experience
    autoRefreshToken: true,
    // Persist session - but we'll handle our own caching for better performance
    persistSession: false, // Disabled - we handle our own localStorage caching
    // Detect session from URL for OAuth flows
    detectSessionInUrl: true,
    // Storage configuration for session management
    storage: {
      getItem: () => null, // Disable built-in storage - we use our own
      setItem: () => {},
      removeItem: () => {},
    },
    // Implicit flow: client storage is stubbed, so PKCE cannot keep its code
    // verifier between sending the reset email and opening the link. Implicit
    // also works when the reset link is opened on another device.
    flowType: 'implicit',
  },
  db: {
    schema: 'public',
  },
  realtime: {
    // Optimize realtime connections
    params: {
      eventsPerSecond: 10,
    },
  },
});

// Add logging for authentication state changes (errors only)
supabase.auth.onAuthStateChange(() => {
  // Authentication state changes logged internally
});

// Health check function
